import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import CloseButton from 'components/shared/CloseButton';
import Search from 'components/shared/Search';
import {
  adminIndexMappings,
  adminIndexReceivedValues,
  adminMappingsQueryKeys,
  DONT_MAP,
  MappingEntry,
  MappingType,
  ReceivedValue,
  WILDCARD,
} from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';

import { Chip, Muted } from './Chips';
import MapPanel, { MapPanelResult } from './MapPanel';
import { AgentInstructions, FallbackRule } from './MappingSettings';
import MappingsTable, { MappingRow } from './MappingsTable';
import NewValues from './NewValues';
import { keepMine, normalizeKey, PendingOperation, removeSlot, TYPE_TITLE, upsertOperation } from './operations';

type Props = {
  configId: string;
  clientId: string;
  ehr: boolean;
  type: MappingType;
  disabled: boolean;
  operations: PendingOperation[];
  onApply: (operations: PendingOperation[]) => void;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
};

export default function MappingsSlideout({
  configId,
  clientId,
  ehr,
  type,
  disabled,
  operations,
  onApply,
  onClose,
  onDirtyChange,
}: Props) {
  const [draft, setDraft] = useState<PendingOperation[]>(operations);
  const [panelKey, setPanelKey] = useState<string | null | undefined>(undefined);
  const [panelMode, setPanelMode] = useState<'map' | 'dont_map'>('map');
  const [search, setSearch] = useState('');
  const recordNoun = ehr ? 'record' : 'patient';

  const mappings = useQuery({
    queryKey: adminMappingsQueryKeys.entries(configId, type),
    queryFn: () => adminIndexMappings(configId, type),
  });
  const receivedValues = useQuery({
    queryKey: adminMappingsQueryKeys.receivedValues(configId, type, 'all'),
    queryFn: () => adminIndexReceivedValues(configId, type, 'all'),
  });
  const newValues = useQuery({
    queryKey: adminMappingsQueryKeys.receivedValues(configId, type, 'new'),
    queryFn: () => adminIndexReceivedValues(configId, type, 'new'),
  });

  const typeOps = useMemo(() => draft.filter((o) => o.type === type), [draft, type]);
  const values = useMemo(() => (receivedValues.data ?? []).filter((v) => !v.dismissed), [receivedValues.data]);

  const entries = useMemo(() => {
    const map = new Map<string, MappingEntry>();
    (mappings.data?.data ?? []).forEach((e) => {
      if (e.key !== WILDCARD) map.set(normalizeKey(e.key), e);
    });
    return map;
  }, [mappings.data]);

  const valuesByKey = useMemo(() => {
    const map = new Map<string, ReceivedValue>();
    values.forEach((v) => map.set(normalizeKey(v.name), v));
    return map;
  }, [values]);

  const rows = useMemo(() => {
    const byKey = new Map<string, MappingRow>();
    entries.forEach((entry, k) => {
      byKey.set(k, { key: entry.key, entry, pending: null, value: valuesByKey.get(k) ?? null });
    });
    typeOps.forEach((op) => {
      if (!('key' in op) || op.op === 'dismiss') return;
      const k = normalizeKey(op.key);
      const row = byKey.get(k) ?? { key: op.key, entry: null, pending: null, value: valuesByKey.get(k) ?? null };
      byKey.set(k, { ...row, pending: op });
    });
    const query = search.trim().toLowerCase();
    const all = [...byKey.values()];
    if (!query) return all;
    return all.filter(
      (r) => r.key.toLowerCase().includes(query) || (r.entry?.resolved?.name ?? '').toLowerCase().includes(query)
    );
  }, [entries, typeOps, valuesByKey, search]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(operations), [draft, operations]);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const stage = useCallback((op: PendingOperation) => setDraft((ops) => upsertOperation(ops, op)), []);
  const undo = useCallback((slot: string) => setDraft((ops) => removeSlot(ops, slot)), []);
  const resolveConflict = useCallback((op: PendingOperation) => stage(keepMine(op)), [stage]);

  const storedTarget = useCallback((key: string) => entries.get(normalizeKey(key))?.target ?? null, [entries]);
  const storedKey = useCallback((key: string) => entries.get(normalizeKey(key))?.key ?? key, [entries]);

  const stageDontMap = useCallback(
    (key: string) => stage({ op: 'dont_map', type, key: storedKey(key), expected: storedTarget(key) }),
    [stage, type, storedKey, storedTarget]
  );

  const openPanel = (key: string | null, mode: 'map' | 'dont_map' = 'map') => {
    setPanelMode(mode);
    setPanelKey(key);
  };

  const dontMap = (key: string) => (ehr && type === 'groups' ? openPanel(key, 'dont_map') : stageDontMap(key));

  const onPanelSave = ({ key, target, label, addToNetwork }: MapPanelResult) => {
    if (target === DONT_MAP) {
      stageDontMap(key);
    } else {
      stage({
        op: 'set',
        type,
        key: storedKey(key),
        target,
        expected: storedTarget(key),
        label,
        ...(addToNetwork ? { addToNetwork: true } : {}),
      });
    }
    setPanelKey(undefined);
  };

  const instructionsOp = typeOps.find((o) => o.op === 'instructions');
  const fallbackOp = typeOps.find((o) => o.op === 'fallback');
  const pendingCount = typeOps.length;
  const loading = mappings.isLoading || receivedValues.isLoading || newValues.isLoading;

  return (
    <PanelContainer>
      <PanelHeader>
        <TitleRow>
          <PanelTitle>{TYPE_TITLE[type]}</PanelTitle>
          {pendingCount > 0 && <Chip $tone='amber'>{pendingCount} pending</Chip>}
        </TitleRow>
        <CloseButton onClick={onClose} />
      </PanelHeader>

      <PanelContent>
        {loading ? (
          <CircularProgress />
        ) : mappings.isError ? (
          <Muted>These mappings couldn&apos;t be loaded. Close and try again.</Muted>
        ) : panelKey !== undefined ? (
          <MapPanel
            configId={configId}
            clientId={clientId}
            ehr={ehr}
            type={type}
            mappingKey={panelKey}
            currentLabel={panelKey ? (entries.get(normalizeKey(panelKey))?.resolved?.name ?? null) : null}
            initialMode={panelMode}
            onCancel={() => setPanelKey(undefined)}
            onSave={onPanelSave}
          />
        ) : (
          <>
            <NewValues
              type={type}
              fresh={newValues.data ?? []}
              entries={entries}
              operations={typeOps}
              recordNoun={recordNoun}
              disabled={disabled}
              onAcknowledge={(entry) =>
                stage({ op: 'acknowledge', type, key: entry.key, expected: entry.target ?? null })
              }
              onMap={(key) => openPanel(key)}
              onDontMap={(key) => dontMap(key)}
              onDismiss={(value) => stage({ op: 'dismiss', type, key: value.name, attrValueId: value.id })}
              onUndo={undo}
              onKeepMine={resolveConflict}
            />

            <AgentInstructions
              title={TYPE_TITLE[type]}
              stored={mappings.data?.instructions ?? null}
              pending={instructionsOp?.op === 'instructions' ? instructionsOp.text : null}
              problem={instructionsOp}
              disabled={disabled}
              onChange={(text) =>
                text === null ? undo(`${type}|instructions`) : stage({ op: 'instructions', type, text })
              }
              onUndo={undo}
            />

            <FallbackRule
              stored={mappings.data?.fallback ?? null}
              pending={fallbackOp?.op === 'fallback' ? fallbackOp.target : undefined}
              problem={fallbackOp}
              disabled={disabled}
              onChange={(target) =>
                target === undefined
                  ? undo(`${type}|fallback`)
                  : stage({ op: 'fallback', type, target, expected: mappings.data?.fallback ?? null })
              }
              onKeepMine={resolveConflict}
              onUndo={undo}
            />

            <SearchBar>
              <Search placeholder='Search mappings' value={search} onChange={setSearch} />
              {!disabled && <Button onClick={() => openPanel(null)}>Add mapping</Button>}
            </SearchBar>
            <MappingsTable
              type={type}
              rows={rows}
              recordNoun={recordNoun}
              disabled={disabled}
              onChange={(row) => openPanel(row.key)}
              onDontMap={(row) => dontMap(row.key)}
              onAcknowledge={(row) =>
                row.entry && stage({ op: 'acknowledge', type, key: row.entry.key, expected: row.entry.target ?? null })
              }
              onRemove={(row) => stage({ op: 'remove', type, key: row.key, expected: row.entry?.target ?? null })}
              onUndo={undo}
              onKeepMine={resolveConflict}
            />
          </>
        )}
      </PanelContent>

      <PanelFooter>
        <Muted>Changes stay pending until you submit.</Muted>
        <ButtonGroup>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply(draft);
            }}
            disabled={disabled || panelKey !== undefined}>
            Apply
          </Button>
        </ButtonGroup>
      </PanelFooter>
    </PanelContainer>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;
